import { Box, Button, Typography } from "@mui/material";
import type { ReactNode } from "react";
import type { StateAction } from "./EmptyState";
import { Link as RouterLink } from "react-router-dom";

/**
 * Inline error card: what failed, one line of help, and Retry. It sits where
 * the content would have been, so the rest of the screen keeps working.
 */
export function ErrorCard({
  title,
  body = "Check your connection.",
  onRetry,
  retryLabel = "Retry",
  retrying = false,
  action,
}: {
  title: string;
  body?: ReactNode;
  onRetry?: () => void;
  retryLabel?: string;
  /** Keep Retry in place (and focus on it) but inert while a retry runs. */
  retrying?: boolean;
  /** A way out instead of (or as well as) Retry, e.g. back to the facility. */
  action?: StateAction;
}) {
  const pillSx = { borderRadius: 999, px: 1.75, color: "text.primary", borderColor: "divider" } as const;
  return (
    <Box
      role="alert"
      data-testid="error-card"
      sx={{
        border: 1,
        borderColor: (theme) => `color-mix(in srgb, ${theme.palette.error.main} 45%, transparent)`,
        bgcolor: "background.paper",
        borderRadius: "12px",
        px: 2,
        py: 1.5,
      }}
    >
      <Typography
        sx={(theme) => ({
          fontSize: "0.9375rem",
          fontWeight: 600,
          // AA (4.5:1) on both surfaces: the deeper red on light, the lighter on dark.
          color: theme.vars ? theme.vars.palette.error.main : theme.palette.error.main,
          ...theme.applyStyles("dark", {
            color: theme.vars ? theme.vars.palette.error.light : theme.palette.error.light,
          }),
        })}
      >
        {title}
      </Typography>
      {body ? (
        <Typography variant="body2" color="text.secondary">
          {body}
        </Typography>
      ) : null}
      {onRetry || action ? (
        <Box sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}>
          {onRetry ? (
            <Button
              type="button"
              size="small"
              variant="outlined"
              onClick={retrying ? undefined : onRetry}
              aria-disabled={retrying || undefined}
              aria-busy={retrying || undefined}
              sx={{ ...pillSx, ...(retrying ? { opacity: 0.6, cursor: "progress" } : {}) }}
            >
              {retryLabel}
            </Button>
          ) : null}
          {action ? (
            action.to ? (
              <Button component={RouterLink} to={action.to} size="small" variant="outlined" sx={pillSx}>
                {action.label}
              </Button>
            ) : (
              <Button type="button" size="small" variant="outlined" onClick={action.onClick} sx={pillSx}>
                {action.label}
              </Button>
            )
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
}
